import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Calculator, Building } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { generateRetentionPDF } from '@/utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateRetention = () => {
  const { tipo: tipoUrl } = useParams(); // 'iva' or 'islr' from URL
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  
  // Determinar tipo inicial desde URL
  const tipoInicial = tipoUrl ? tipoUrl.toUpperCase() : 'IVA';
  
  const [formData, setFormData] = useState({
    proveedor_id: '',
    tipo: tipoInicial,
    categoria: '',
    monto_total_factura: '',
    subtotal: '',
    iva_factura: '',
    porcentaje_retencion: '',
    monto_retenido: '',
    concepto: '',
    numero_factura: '',
    numero_control: '',
    fecha_dia: '',
    fecha_mes: '',
    fecha_anio: '',
  });
  const [previewNumero, setPreviewNumero] = useState('');
  const [usarFechaPersonalizada, setUsarFechaPersonalizada] = useState(false);
  const [fechaRetencion, setFechaRetencion] = useState({ dia: '', mes: '', anio: '' });

  useEffect(() => {
    fetchProviders();
    fetchConfigs();
    fetchEmpresa();
  }, []);

  // Resetear formulario cuando cambia el tipo desde la URL
  useEffect(() => {
    const nuevoTipo = tipoUrl ? tipoUrl.toUpperCase() : 'IVA';
    setFormData({
      proveedor_id: '',
      tipo: nuevoTipo,
      categoria: '',
      monto_total_factura: '',
      subtotal: '',
      iva_factura: '',
      porcentaje_retencion: '',
      monto_retenido: '',
      concepto: '',
      numero_factura: '',
      numero_control: '',
      fecha_dia: '',
      fecha_mes: '',
      fecha_anio: '',
    });
    setUsarFechaPersonalizada(false);
    setFechaRetencion({ dia: '', mes: '', anio: '' });
  }, [tipoUrl]);

  useEffect(() => {
    if (empresa && formData.tipo) {
      const fecha = new Date();
      const anio = fecha.getFullYear();
      const mes = String(fecha.getMonth() + 1).padStart(2, '0');
      const secuencial = formData.tipo === 'IVA' ? empresa.secuencia_iva : empresa.secuencia_islr;
      const numeroCompleto = `${anio}${mes}${String(secuencial).padStart(8, '0')}`;
      setPreviewNumero(numeroCompleto);
    }
  }, [empresa, formData.tipo]);

  useEffect(() => {
    calculateAmounts();
  }, [formData.subtotal, formData.tipo]);

  useEffect(() => {
    calculateRetention();
  }, [formData.monto_total_factura, formData.porcentaje_retencion, formData.tipo]);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/proveedores`);
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/configuracion`);
      setConfigs(response.data.filter(c => c.activo));
    } catch (error) {
      console.error('Error fetching configs:', error);
    }
  };

  const fetchEmpresa = async () => {
    try {
      const response = await axios.get(`${API}/empresa`);
      setEmpresa(response.data);
    } catch (error) {
      console.error('Error fetching empresa:', error);
    }
  };

  // Helper para parsear números con coma o punto como decimal
  const parseNumber = (value) => {
    if (!value) return 0;
    // Reemplazar coma por punto para que parseFloat lo entienda
    const normalized = value.toString().replace(',', '.');
    return parseFloat(normalized) || 0;
  };

  const calculateAmounts = () => {
    // Solo calcular IVA para retenciones de tipo IVA
    if (formData.tipo !== 'IVA') return;
    
    const subtotal = parseNumber(formData.subtotal);
    if (subtotal === 0) return;
    
    const iva = subtotal * 0.16; // IVA 16%
    const total = subtotal + iva;
    
    setFormData(prev => ({
      ...prev,
      iva_factura: iva.toFixed(2),
      monto_total_factura: total.toFixed(2),
    }));
  };

  const calculateRetention = () => {
    const percentage = parseNumber(formData.porcentaje_retencion);
    
    let retention = 0;
    if (formData.tipo === 'IVA') {
      // Para IVA, el porcentaje se aplica sobre el IVA de la factura
      const ivaFactura = parseNumber(formData.iva_factura);
      retention = (ivaFactura * percentage) / 100;
    } else {
      // Para ISLR, el porcentaje se aplica sobre el subtotal/monto total
      const subtotal = parseNumber(formData.subtotal);
      retention = (subtotal * percentage) / 100;
    }
    
    setFormData(prev => ({ ...prev, monto_retenido: retention.toFixed(2) }));
  };

  const handleTipoChange = (tipo) => {
    setFormData({
      ...formData,
      tipo,
      categoria: '',
      porcentaje_retencion: '',
    });
  };

  const handleCategoriaChange = (categoria) => {
    const config = configs.find(c => c.tipo === formData.tipo && c.categoria === categoria);
    setFormData({
      ...formData,
      categoria,
      porcentaje_retencion: config ? config.porcentaje.toString() : '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Construir la fecha de factura en formato ISO (YYYY-MM-DD)
    const dia = formData.fecha_dia.padStart(2, '0');
    const mes = formData.fecha_mes.padStart(2, '0');
    const anio = '20' + formData.fecha_anio.padStart(2, '0');
    const fechaFactura = `${anio}-${mes}-${dia}`;
    
    // Construir la fecha de retención (personalizada o fecha actual)
    let fechaRetencionISO = null;
    if (usarFechaPersonalizada && fechaRetencion.dia && fechaRetencion.mes && fechaRetencion.anio) {
      const diaRet = fechaRetencion.dia.padStart(2, '0');
      const mesRet = fechaRetencion.mes.padStart(2, '0');
      const anioRet = '20' + fechaRetencion.anio.padStart(2, '0');
      fechaRetencionISO = `${anioRet}-${mesRet}-${diaRet}`;
    }
    
    try {
      const payload = {
        ...formData,
        fecha_factura: fechaFactura,
        monto_total_factura: parseFloat(formData.monto_total_factura),
        subtotal: parseFloat(formData.subtotal),
        iva_factura: parseFloat(formData.iva_factura),
        porcentaje_retencion: parseFloat(formData.porcentaje_retencion),
        monto_retenido: parseFloat(formData.monto_retenido),
      };
      
      // Solo enviar fecha personalizada si está habilitada
      if (fechaRetencionISO) {
        payload.fecha_retencion = fechaRetencionISO;
      }
      
      const response = await axios.post(`${API}/retenciones`, payload);
      
      toast.success('Retención creada exitosamente');
      
      // Generate and download PDF
      const provider = providers.find(p => p.id === formData.proveedor_id);
      generateRetentionPDF(response.data, provider, empresa);
      
      // Reset form
      setFormData({
        proveedor_id: '',
        tipo: formData.tipo,
        categoria: '',
        monto_total_factura: '',
        subtotal: '',
        iva_factura: '',
        porcentaje_retencion: '',
        monto_retenido: '',
        concepto: '',
        numero_factura: '',
        numero_control: '',
        fecha_dia: '',
        fecha_mes: '',
        fecha_anio: '',
      });
      setUsarFechaPersonalizada(false);
      setFechaRetencion({ dia: '', mes: '', anio: '' });
      
      setTimeout(() => navigate('/retenciones/historial'), 1500);
    } catch (error) {
      console.error('Error creating retention:', error);
      toast.error('Error al crear retención');
    }
  };

  const availableCategories = configs.filter(c => c.tipo === formData.tipo);

  const getPageTitle = () => {
    if (tipoUrl === 'iva') return 'Nueva Retención de IVA';
    if (tipoUrl === 'islr') return 'Nueva Retención de ISLR';
    return 'Nueva Retención';
  };

  const getPageSubtitle = () => {
    if (tipoUrl === 'iva') return 'Generar comprobante de retención de IVA';
    if (tipoUrl === 'islr') return 'Generar comprobante de retención de ISLR - Decreto 1808';
    return 'Generar comprobante de retención IVA o ISLR';
  };

  return (
    <div className="p-8" data-testid="create-retention-page">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-heading">{getPageTitle()}</h1>
          {tipoUrl && (
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              tipoUrl === 'iva' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
            }`}>
              {tipoUrl.toUpperCase()}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-2">{getPageSubtitle()}</p>
      </div>

      <div className="max-w-4xl">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Building className="text-blue-600" size={20} />
            <h2 className="text-lg font-semibold text-slate-900">Datos del Agente de Retención</h2>
          </div>
          {empresa && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600">Razón Social:</span>
                  <p className="font-medium text-slate-900">{empresa.nombre}</p>
                </div>
                <div>
                  <span className="text-slate-600">RIF:</span>
                  <p className="font-mono font-medium text-slate-900">{empresa.rif}</p>
                </div>
                <div>
                  <span className="text-slate-600">Dirección:</span>
                  <p className="font-medium text-slate-900">{empresa.direccion}</p>
                </div>
                <div>
                  <span className="text-slate-600">Teléfono:</span>
                  <p className="font-mono font-medium text-slate-900">{empresa.telefono}</p>
                </div>
              </div>
              <div className="mt-3">
                <a href="/configuracion" className="text-sm text-blue-600 hover:underline">
                  Editar datos de la empresa
                </a>
              </div>
            </div>
          )}
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-md font-semibold text-slate-900">Número de Comprobante que se Generará</h3>
              <p className="text-xs text-slate-500 mt-1">Según la secuencia configurada para {formData.tipo}</p>
            </div>
            <div className="bg-blue-50 border-2 border-blue-600 rounded-lg px-6 py-4">
              <div className="flex items-center space-x-2">
                <div className="bg-slate-300 rounded px-3 py-2 font-mono text-lg font-bold text-slate-700">
                  {previewNumero.substring(0, 6)}
                </div>
                <div className="bg-blue-100 rounded px-3 py-2 font-mono text-lg font-bold text-blue-700">
                  {previewNumero.substring(6)}
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2 text-center">
                <span className="font-mono text-slate-700">AAAAMM</span> + <span className="font-mono text-blue-700">SSSSSSSS</span>
              </p>
            </div>
          </div>
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              <strong>Nota:</strong> El número de comprobante se genera automáticamente. Si necesitas cambiar el secuencial, 
              ve a <a href="/configuracion" className="underline font-semibold">Configuración</a> antes de crear la retención.
            </p>
          </div>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Datos de la Factura</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="proveedor">Proveedor (Sujeto Retenido) *</Label>
                  <Select
                    value={formData.proveedor_id}
                    onValueChange={(value) => setFormData({ ...formData, proveedor_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="retention-proveedor-select">
                      <SelectValue placeholder="Seleccionar proveedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.nombre} - {provider.rif}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Solo mostrar selector de tipo si no viene de URL */}
                {!tipoUrl && (
                  <div>
                    <Label htmlFor="tipo">Tipo de Retención *</Label>
                    <Select value={formData.tipo} onValueChange={handleTipoChange} required>
                      <SelectTrigger data-testid="retention-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IVA">IVA</SelectItem>
                        <SelectItem value="ISLR">ISLR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Mostrar tipo fijo cuando viene de URL */}
                {tipoUrl && (
                  <div>
                    <Label>Tipo de Retención</Label>
                    <div className={`flex items-center h-9 px-3 rounded-md border ${
                      tipoUrl === 'iva' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'
                    }`}>
                      <span className={`font-medium ${
                        tipoUrl === 'iva' ? 'text-blue-700' : 'text-amber-700'
                      }`}>
                        {formData.tipo}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <Label htmlFor="numero_factura">Número de Factura *</Label>
                  <Input
                    id="numero_factura"
                    data-testid="retention-factura-input"
                    value={formData.numero_factura}
                    onChange={(e) => setFormData({ ...formData, numero_factura: e.target.value })}
                    placeholder="Ej: 00001234"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="numero_control">Número de Control</Label>
                  <div className="flex items-center">
                    <span className="bg-slate-100 border border-r-0 border-slate-300 rounded-l-md px-3 py-2 text-slate-600 font-mono text-sm">
                      00-
                    </span>
                    <Input
                      id="numero_control"
                      data-testid="retention-control-input"
                      value={formData.numero_control.replace(/^00-/, '')}
                      onChange={(e) => setFormData({ ...formData, numero_control: '00-' + e.target.value.replace(/^00-/, '') })}
                      placeholder="12345678"
                      className="rounded-l-none font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <Label htmlFor="fecha_factura">Fecha de Factura *</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      id="fecha_dia"
                      data-testid="fecha-dia-input"
                      type="text"
                      inputMode="numeric"
                      maxLength="2"
                      placeholder="DD"
                      value={formData.fecha_dia || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                        const num = parseInt(val) || 0;
                        if (num <= 31) {
                          setFormData({ ...formData, fecha_dia: val });
                        }
                      }}
                      onBlur={(e) => {
                        const val = e.target.value;
                        if (val && val.length === 1) {
                          setFormData({ ...formData, fecha_dia: val.padStart(2, '0') });
                        }
                      }}
                      className="w-14 text-center font-mono"
                      required
                    />
                    <span className="text-slate-400 font-bold">/</span>
                    <Input
                      id="fecha_mes"
                      data-testid="fecha-mes-input"
                      type="text"
                      inputMode="numeric"
                      maxLength="2"
                      placeholder="MM"
                      value={formData.fecha_mes || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                        const num = parseInt(val) || 0;
                        if (num <= 12) {
                          setFormData({ ...formData, fecha_mes: val });
                        }
                      }}
                      onBlur={(e) => {
                        const val = e.target.value;
                        if (val && val.length === 1) {
                          setFormData({ ...formData, fecha_mes: val.padStart(2, '0') });
                        }
                      }}
                      className="w-14 text-center font-mono"
                      required
                    />
                    <span className="text-slate-400 font-bold">/</span>
                    <span className="bg-slate-100 border border-slate-300 rounded-l-md px-2 py-2 text-slate-600 font-mono text-sm">
                      20
                    </span>
                    <Input
                      id="fecha_anio"
                      data-testid="fecha-anio-input"
                      type="text"
                      inputMode="numeric"
                      maxLength="2"
                      placeholder="AA"
                      value={formData.fecha_anio || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                        setFormData({ ...formData, fecha_anio: val });
                      }}
                      className="w-14 text-center font-mono rounded-l-none border-l-0"
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Formato: DD / MM / 20AA</p>
                </div>

                {/* Fecha de Retención personalizada */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="usar_fecha_personalizada"
                      checked={usarFechaPersonalizada}
                      onChange={(e) => setUsarFechaPersonalizada(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <Label htmlFor="usar_fecha_personalizada" className="cursor-pointer">
                      Usar fecha de retención diferente a hoy
                    </Label>
                  </div>
                  
                  {usarFechaPersonalizada && (
                    <>
                      <div className="flex items-center gap-1">
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength="2"
                          placeholder="DD"
                          value={fechaRetencion.dia}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                            const num = parseInt(val) || 0;
                            if (num <= 31) {
                              setFechaRetencion({ ...fechaRetencion, dia: val });
                            }
                          }}
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val && val.length === 1) {
                              setFechaRetencion({ ...fechaRetencion, dia: val.padStart(2, '0') });
                            }
                          }}
                          className="w-14 text-center font-mono"
                        />
                        <span className="text-slate-400 font-bold">/</span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength="2"
                          placeholder="MM"
                          value={fechaRetencion.mes}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                            const num = parseInt(val) || 0;
                            if (num <= 12) {
                              setFechaRetencion({ ...fechaRetencion, mes: val });
                            }
                          }}
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val && val.length === 1) {
                              setFechaRetencion({ ...fechaRetencion, mes: val.padStart(2, '0') });
                            }
                          }}
                          className="w-14 text-center font-mono"
                        />
                        <span className="text-slate-400 font-bold">/</span>
                        <span className="bg-slate-100 border border-slate-300 rounded-l-md px-2 py-2 text-slate-600 font-mono text-sm">
                          20
                        </span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength="2"
                          placeholder="AA"
                          value={fechaRetencion.anio}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                            setFechaRetencion({ ...fechaRetencion, anio: val });
                          }}
                          className="w-14 text-center font-mono rounded-l-none border-l-0"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Fecha del comprobante de retención</p>
                    </>
                  )}
                  
                  {!usarFechaPersonalizada && (
                    <p className="text-xs text-slate-500">Se usará la fecha de hoy para la retención</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="concepto">Concepto / Descripción de la Operación *</Label>
                <Input
                  id="concepto"
                  data-testid="retention-concepto-input"
                  value={formData.concepto}
                  onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                  placeholder="Descripción detallada del servicio o producto"
                  required
                />
              </div>
            </div>

            {/* ============= SECCIÓN CÁLCULOS IVA ============= */}
            {formData.tipo === 'IVA' && (
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  <span className="inline-flex items-center gap-2">
                    Cálculos de la Factura
                    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">IVA</span>
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="subtotal">Subtotal (Base Imponible) Bs. *</Label>
                    <Input
                      id="subtotal"
                      type="text"
                      data-testid="retention-subtotal-input"
                      value={formData.subtotal}
                      onChange={(e) => setFormData({ ...formData, subtotal: e.target.value })}
                      className="font-mono"
                      placeholder="Monto sin IVA"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="iva_factura">IVA (16%) Bs.</Label>
                    <Input
                      id="iva_factura"
                      type="text"
                      data-testid="retention-iva-input"
                      value={formData.iva_factura}
                      readOnly
                      className="bg-blue-50 font-mono border-blue-200"
                    />
                    <p className="text-xs text-slate-500 mt-1">Calculado automáticamente</p>
                  </div>

                  <div>
                    <Label htmlFor="monto_total_factura">Monto Total Factura Bs.</Label>
                    <Input
                      id="monto_total_factura"
                      type="number"
                      step="0.01"
                      data-testid="retention-total-input"
                      value={formData.monto_total_factura}
                      readOnly
                      className="bg-blue-50 font-mono font-bold text-blue-700 border-blue-200"
                    />
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Retención IVA:</strong> Se calcula sobre el monto del IVA (16% del subtotal). 
                    Los porcentajes comunes son 75% o 100% del IVA.
                  </p>
                </div>
              </div>
            )}

            {/* ============= SECCIÓN CÁLCULOS ISLR ============= */}
            {formData.tipo === 'ISLR' && (
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  <span className="inline-flex items-center gap-2">
                    Base Imponible
                    <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">ISLR</span>
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="monto_total_factura">Base Imponible Bs. *</Label>
                    <Input
                      id="monto_total_factura"
                      type="text"
                      data-testid="retention-total-input"
                      value={formData.monto_total_factura}
                      onChange={(e) => {
                        const monto = e.target.value;
                        setFormData({
                          ...formData, 
                          monto_total_factura: monto,
                          subtotal: monto,
                          iva_factura: '0'
                        });
                      }}
                      className="font-mono"
                      placeholder="Ingrese el monto"
                      required
                    />
                  </div>

                  <div className="flex items-end">
                    <div className="w-full p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Base Imponible:</strong> {formData.monto_total_factura ? `Bs. ${parseNumber(formData.monto_total_factura).toLocaleString('es-VE', { minimumFractionDigits: 2 })}` : 'Bs. 0,00'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <strong>Retención ISLR (Decreto 1808):</strong> Se calcula sobre la base imponible de la factura. 
                    El porcentaje varía según el tipo de servicio o actividad (ej: 1%, 2%, 3%, 5%, etc.).
                  </p>
                </div>
              </div>
            )}

            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                <span className="inline-flex items-center gap-2">
                  Datos de la Retención
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    formData.tipo === 'IVA' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {formData.tipo}
                  </span>
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="categoria">
                    {formData.tipo === 'IVA' ? 'Porcentaje de Retención IVA *' : 'Categoría ISLR (Decreto 1808) *'}
                  </Label>
                  <Select
                    value={formData.categoria}
                    onValueChange={handleCategoriaChange}
                    required
                  >
                    <SelectTrigger data-testid="retention-categoria-select">
                      <SelectValue placeholder={formData.tipo === 'IVA' ? 'Seleccionar % retención' : 'Seleccionar categoría ISLR'} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.map((config) => (
                        <SelectItem key={config.id} value={config.categoria}>
                          {config.categoria} ({config.porcentaje}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {availableCategories.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No hay categorías de {formData.tipo} configuradas. <a href="/configuracion" className="underline font-semibold">Configurar ahora</a>
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="porcentaje_retencion">Porcentaje Aplicado (%)</Label>
                  <Input
                    id="porcentaje_retencion"
                    type="number"
                    step="0.01"
                    data-testid="retention-porcentaje-input"
                    value={formData.porcentaje_retencion}
                    onChange={(e) => setFormData({ ...formData, porcentaje_retencion: e.target.value })}
                    className={`font-mono ${formData.tipo === 'IVA' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}
                    readOnly
                  />
                  <p className="text-xs text-slate-500 mt-1">Se carga automáticamente según la categoría</p>
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="monto_retenido">
                  {formData.tipo === 'IVA' ? 'IVA Retenido Bs.' : 'ISLR Retenido Bs.'}
                </Label>
                <Input
                  id="monto_retenido"
                  type="number"
                  step="0.01"
                  data-testid="retention-monto-input"
                  value={formData.monto_retenido}
                  readOnly
                  className={`font-mono font-bold text-lg ${
                    formData.tipo === 'IVA' 
                      ? 'bg-blue-100 border-blue-300 text-blue-800' 
                      : 'bg-amber-100 border-amber-300 text-amber-800'
                  }`}
                />
              </div>

              <div className={`rounded-lg p-4 mt-4 ${
                formData.tipo === 'IVA' ? 'bg-blue-50 border border-blue-200' : 'bg-amber-50 border border-amber-200'
              }`}>
                <div className={`flex items-center space-x-2 text-sm ${
                  formData.tipo === 'IVA' ? 'text-blue-700' : 'text-amber-700'
                }`}>
                  <Calculator size={18} />
                  <span className="font-medium">Fórmula:</span>
                  <span className="font-mono">
                    {formData.tipo === 'IVA' 
                      ? `IVA (Bs. ${formData.iva_factura || '0'}) × ${formData.porcentaje_retencion || '0'}% = Bs. ${formData.monto_retenido || '0.00'}`
                      : `Monto Total (Bs. ${formData.monto_total_factura || '0'}) × ${formData.porcentaje_retencion || '0'}% = Bs. ${formData.monto_retenido || '0.00'}`
                    }
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="retention-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="retention-submit-btn"
                className={`text-white shadow-md hover:shadow-lg ${
                  formData.tipo === 'IVA' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                <FileText size={20} className="mr-2" />
                Generar Retención {formData.tipo} y Descargar PDF
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CreateRetention;